import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

import { APP_CONSTANTS } from './configs/app.constants';
import { EchoService } from './core/services/echo.service';
import { AuthService } from './core/api/auth/auth.service';
import { OnlineUsersService } from './core/services/online-users.service';
import { UserSessionService } from './core/services/user-session.service';
import { PasswordChangeCheckService } from './core/services/password-change-check.service';
import { ModalService } from './shared/components/modal/modal.service';
import { SessionRevokedModalComponent } from './shared/components/session-revoked-modal/session-revoked-modal.component';
import { AccountDeactivatedModalComponent } from './shared/components/account-deactivated-modal/account-deactivated-modal.component';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterOutlet],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    private titleService = inject(Title);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private echoService = inject(EchoService);
    private authService = inject(AuthService);
    private onlineUsersService = inject(OnlineUsersService);
    private userSessionService = inject(UserSessionService);
    private passwordCheckService = inject(PasswordChangeCheckService);
    private modalService = inject(ModalService);

    private isInitialized = false;
    private sessionRevokedModalId: number | null = null;
    private accountDeactivatedModalId: number | null = null;
    private currentLoginSessionUuid: string | null = null;

    constructor() {
        // Reset initialization flag when user logs out
        this.authService.logout$.subscribe(() => {
            console.log('[App] User logged out, resetting initialization flag');
            this.isInitialized = false;
        });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => {
                    let route = this.activatedRoute;
                    while (route.firstChild) route = route.firstChild;
                    return route;
                }),
                mergeMap((route) => route.data),
            )
            .subscribe((data) => {
                if (data['title']) {
                    const appTitle = `${APP_CONSTANTS.APP_TITLE} - ${data['title']}`;
                    this.titleService.setTitle(appTitle);
                }

                // Initialize authenticated user services after navigation if user is set
                if (this.authService.user() && !this.isInitialized) {
                    this.initializeAuthenticatedUser();
                }

                // Check account suspension on every navigation
                if (this.authService.user()) {
                    this.checkAccountSuspension();
                }

                // Check password expiry on route change (if user is set and not on change-password page)
                if (this.authService.user() && this.router.url !== '/change-password') {
                    this.passwordCheckService.checkPasswordExpiry();
                }
            });
    }

    ngOnInit() {
        // Check if user is authenticated by trying to fetch current user
        // The HTTP-only cookie will be sent automatically with the request
        if (this.authService.user()) {
            // User already loaded in memory
            this.initializeAuthenticatedUser();
        } else {
            // Try to get current user (cookie will be sent automatically)
            this.authService.getCurrentUser().subscribe({
                next: (response) => {
                    if (response.data) {
                        // User is authenticated
                        this.authService.setUser(response.data);
                        this.initializeAuthenticatedUser();
                    }
                },
                error: () => {
                    // No valid session, user will be redirected by guards if needed
                },
            });
        }
    }

    initializeAuthenticatedUser(): void {
        // Prevent multiple initializations
        if (this.isInitialized) return;
        this.isInitialized = true;

        console.log('[App] Initializing authenticated user services');

        // Check if user account is suspended due to involuntary delisting
        this.checkAccountSuspension();

        // Start session tracking
        this.userSessionService.startTracking();

        // Connect to Echo (WebSocket)
        console.log('[App] Connecting Echo...');
        this.echoService.connect();

        // Start online user tracking and session revocation listener when Echo is ready
        this.echoService.connectionReady().subscribe((isReady) => {
            if (isReady) {
                console.log('[App] Echo connected successfully, starting online user tracking');
                this.onlineUsersService.startTracking();

                // Subscribe to events
                const user = this.authService.user();
                if (user?.id) {
                    this.subscribeToSessionRevocation(user.id);
                    this.subscribeToAccountDeactivation(user.id);
                }
            }
        });
    }

    /**
     * Subscribe to session revocation events for the current user
     * This will automatically logout the user if they login from another device
     */
    private subscribeToSessionRevocation(userId: string): void {
        console.log('[App] Subscribing to session revocation for user:', userId);

        // Get current login-session identifier from storage (set during login)
        this.currentLoginSessionUuid = sessionStorage.getItem('login_session_uuid');
        console.log('[App] Current login session UUID:', this.currentLoginSessionUuid);

        this.echoService.subscribeToSessionRevocation(userId, (event) => {
            console.log('[App] Session revoked event received:', event);
            console.log('[App] Event login session UUID:', event.currentLoginSessionUuid);
            console.log('[App] Stored login session UUID:', this.currentLoginSessionUuid);
            console.log(
                '[App] Login session UUIDs match?',
                event.currentLoginSessionUuid === this.currentLoginSessionUuid,
            );

            // Per-login UUID differs on every successful login.
            if (
                event.currentLoginSessionUuid &&
                this.currentLoginSessionUuid &&
                event.currentLoginSessionUuid === this.currentLoginSessionUuid
            ) {
                console.log('[App] Ignoring session revocation for current session');
                return;
            }

            console.warn('[App] Session revoked:', event.message);

            // Show session revoked modal with custom message
            this.showSessionRevokedModal(event.message);

            // Perform logout
            this.handleSessionRevoked();
        });
    }
    /**
     * Subscribe to account deactivation events for the current user
     * This will automatically logout the user if their account is deactivated
     */
    private subscribeToAccountDeactivation(userId: string): void {
        console.log('[App] Subscribing to account deactivation for user:', userId);

        this.echoService.subscribeToAccountDeactivation(userId, (event) => {
            console.log('[App] Account deactivated event received:', event);

            // Show account deactivated modal with custom message
            this.showAccountDeactivatedModal(event.message);

            // Perform logout
            this.handleAccountDeactivated();
        });
    }
    /**
     * Show session revoked modal
     */
    private showSessionRevokedModal(message?: string): void {
        this.sessionRevokedModalId = this.modalService.open({
            title: 'Session Terminated',
            contentComponent: SessionRevokedModalComponent,
            contentData: message ? { message } : undefined,
            size: 'md',
            position: 'center',
            animation: 'fade',
            backdropDismiss: false,
            keyboardClose: false,
            showCloseButton: false,
        });
    }

    /**
     * Show account deactivated modal
     */
    private showAccountDeactivatedModal(message?: string): void {
        this.accountDeactivatedModalId = this.modalService.open({
            title: 'Session Terminated',
            contentComponent: AccountDeactivatedModalComponent,
            contentData: { message },
            size: 'md',
            position: 'center',
            animation: 'fade',
            backdropDismiss: false,
            keyboardClose: false,
            showCloseButton: false,
        });
    }

    /**
     * Handle account deactivation by cleaning up and preparing for logout
     */
    private handleAccountDeactivated(): void {
        console.log('[App] Handling account deactivation...');

        // Stop all services
        this.onlineUsersService.stopTracking();
        this.userSessionService.stopTracking();

        // Unsubscribe from events
        const user = this.authService.user();
        if (user?.id) {
            this.echoService.unsubscribeFromSessionRevocation(user.id);
            this.echoService.unsubscribeFromAccountDeactivation(user.id);
        }

        // Disconnect Echo
        this.echoService.disconnect();

        // Clear user data
        this.authService.clearUser();
        this.authService.clearUserCache();

        // Clear device UUID
        sessionStorage.removeItem('device_uuid');
        sessionStorage.removeItem('login_session_uuid');

        // Reset initialization flag
        this.isInitialized = false;

        // Note: Modal stays open - user must click "Return to Login" button
    }

    /**
     * Handle session revocation by cleaning up and redirecting to login
     */
    private handleSessionRevoked(): void {
        console.log('[App] Handling session revocation...');

        // Stop all services
        this.onlineUsersService.stopTracking();
        this.userSessionService.stopTracking();

        // Unsubscribe from events
        const user = this.authService.user();
        if (user?.id) {
            this.echoService.unsubscribeFromSessionRevocation(user.id);
            this.echoService.unsubscribeFromAccountDeactivation(user.id);
        }

        // Disconnect Echo
        this.echoService.disconnect();

        // Clear user data
        this.authService.clearUser();
        this.authService.clearUserCache();

        // Clear device UUID
        sessionStorage.removeItem('device_uuid');
        sessionStorage.removeItem('login_session_uuid');

        // Reset initialization flag
        this.isInitialized = false;

        // Note: Modal stays open - user must click "Return to Login" button
        // This prevents automatic redirect and gives user time to read the message
    }

    checkAccountSuspension(): void {
        const user: any = this.authService.user();
        if (user && user.isUserTemporarilySuspended) {
            // Redirect to account suspended page if not already there
            if (!this.router.url.includes('/account-suspended')) {
                console.log('Account suspended, redirecting to account-suspended page');
                this.router.navigate(['/account-suspended']);
            }
        }
    }

    ngOnDestroy() {
        // Close session revoked modal if open
        if (this.sessionRevokedModalId !== null) {
            this.modalService.close(this.sessionRevokedModalId);
            this.sessionRevokedModalId = null;
        }

        // Close account deactivated modal if open
        if (this.accountDeactivatedModalId !== null) {
            this.modalService.close(this.accountDeactivatedModalId);
            this.accountDeactivatedModalId = null;
        }

        // Unsubscribe from events before destroying
        const user = this.authService.user();
        if (user?.id) {
            this.echoService.unsubscribeFromSessionRevocation(user.id);
            this.echoService.unsubscribeFromAccountDeactivation(user.id);
        }

        this.onlineUsersService.stopTracking();
        this.userSessionService.stopTracking();
        this.echoService.disconnect();
    }
}
